import { ButtonHTMLAttributes, ReactNode } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost'
  children: ReactNode
  fullWidth?: boolean
}

export default function Button({ 
  variant = 'primary', 
  children, 
  fullWidth = false,
  disabled,
  style,
  ...props 
}: ButtonProps) {
  const baseStyles: React.CSSProperties = {
    padding: '0.875rem 2rem',
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: '2rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled ? 0.5 : 1,
    ...style,
  }

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: disabled ? 'var(--base-300)' : 'var(--base-400)',
      color: disabled ? 'var(--color-text-secondary)' : 'var(--color-text)',
    },
    secondary: {
      border: '2px solid var(--color-border)',
      backgroundColor: 'var(--base-100)',
      color: 'var(--color-text)',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-secondary)',
      padding: '0.5rem 1rem',
    }
  }

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    
    if (variant === 'primary') {
      e.currentTarget.style.backgroundColor = 'var(--color-primary)'
      e.currentTarget.style.color = 'white'
    } else if (variant === 'secondary') {
      e.currentTarget.style.backgroundColor = 'var(--base-300)'
    } else if (variant === 'ghost') {
      e.currentTarget.style.color = 'var(--color-text)'
    }
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    
    if (variant === 'primary') {
      e.currentTarget.style.backgroundColor = 'var(--base-400)'
      e.currentTarget.style.color = 'var(--color-text)'
    } else if (variant === 'secondary') {
      e.currentTarget.style.backgroundColor = 'var(--base-100)'
    } else if (variant === 'ghost') {
      e.currentTarget.style.color = 'var(--color-text-secondary)'
    }
  }

  return (
    <button
      {...props}
      disabled={disabled}
      style={{ ...baseStyles, ...variantStyles[variant] }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </button>
  )
}

